import { useState } from 'react';

import { getRosterIdentityMainLinkedIdentity } from '../../../util/identity';

/**
 * Handles address book records selection.
 *
 * @param {Object} [options]
 * @param {boolean} [options.requireAddress=true] Whether a record needs an address to be selectable.
 * @returns {{onRosterIdentityToggle: onRosterIdentityToggle, selectedRosterIdentities: *[], setWaldogramConfigOpen: React.Dispatch<React.SetStateAction<boolean>>, waldogramConfigOpen: boolean, onRosterIdentitySelect: onRosterIdentitySelect, isRosterIdentitySelectable: (function(*): boolean), isRosterIdentitySelected: (function(*): boolean), onRosterIdentityDeselect: onRosterIdentityDeselect}}
 */
const useAddressBookSelect = ({ requireAddress = true } = {}) => {
  const [selectedRosterIdentities, setSelectedRosterIdentities] = useState([]);
  const [waldogramConfigOpen, setWaldogramConfigOpen] = useState(false);

  const isRosterIdentitySelectable = rosterIdentity => {
    if (!rosterIdentity) {
      return false;
    }

    if (!requireAddress) {
      return true;
    }

    const mainIdentity = getRosterIdentityMainLinkedIdentity(rosterIdentity);

    return mainIdentity?.addresses?.records?.length > 0;
  };

  const isRosterIdentitySelected = rosterIdentity => {
    if (!rosterIdentity) {
      return false;
    }

    return selectedRosterIdentities.some(
      selectedRosterIdentity => selectedRosterIdentity.id === rosterIdentity.id,
    );
  };

  const onRosterIdentitySelect = rosterIdentity => {
    if (!rosterIdentity) {
      return;
    }

    if (
      isRosterIdentitySelected(rosterIdentity) ||
      !isRosterIdentitySelectable(rosterIdentity)
    ) {
      return;
    }

    setSelectedRosterIdentities(prev => [...prev, rosterIdentity]);
  };

  const onRosterIdentityDeselect = rosterIdentity => {
    if (!rosterIdentity) {
      return;
    }

    setSelectedRosterIdentities(prev =>
      prev.filter(
        selectedRosterIdentity =>
          selectedRosterIdentity.id !== rosterIdentity.id,
      ),
    );
  };

  const onRosterIdentitiesClear = () => {
    setSelectedRosterIdentities([]);
  };

  // Drops selections that are no longer part of the given records, so a
  // selection can be kept in sync with the rows currently on screen.
  const onRosterIdentitiesPrune = (rosterIdentities = []) => {
    setSelectedRosterIdentities(prev => {
      const next = prev.filter(selectedRosterIdentity =>
        rosterIdentities.some(
          rosterIdentity => rosterIdentity.id === selectedRosterIdentity.id,
        ),
      );

      return next.length === prev.length ? prev : next;
    });
  };

  const onRosterIdentityToggle = rosterIdentity => {
    if (!isRosterIdentitySelected(rosterIdentity)) {
      onRosterIdentitySelect(rosterIdentity);
    } else {
      onRosterIdentityDeselect(rosterIdentity);
    }
  };

  return {
    selectedRosterIdentities,
    isRosterIdentitySelectable,
    isRosterIdentitySelected,
    onRosterIdentitySelect,
    onRosterIdentityDeselect,
    onRosterIdentitiesClear,
    onRosterIdentitiesPrune,
    onRosterIdentityToggle,
    waldogramConfigOpen,
    setWaldogramConfigOpen,
  };
};

export default useAddressBookSelect;
